/**
 * Progress photos, stored in IndexedDB.
 *
 * Photos don't go in localStorage: it's a ~5 MB string store and a single phone
 * photo would blow the whole budget. IndexedDB holds binary blobs and has room
 * to spare.
 *
 * They never leave the device — there's no upload anywhere. That also means
 * they're not in the JSON backup (which would be a base64 monster), so if you
 * wipe site data the photos go with it. The gallery says so.
 *
 * For fat loss specifically these are worth more than the scale. Weight bounces
 * a kilo a day on water and food; a photo eight weeks apart doesn't lie.
 */

const DB_NAME = 'iron-ledger-photos';
const DB_VERSION = 1;
const STORE = 'photos';

export interface PhotoMeta {
  id: string;
  /** Local calendar date, YYYY-MM-DD. */
  date: string;
  weightKg?: number;
  note?: string;
  createdAt: string;
}

export interface ProgressPhoto extends PhotoMeta {
  blob: Blob;
}

/** Longest edge after downscaling. Plenty for comparison, small on disk. */
const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.82;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('This browser has no IndexedDB, so photos cannot be saved.'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('date', 'date');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open the photo store.'));
  });
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = run(transaction.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('Photo store request failed.'));
        transaction.oncomplete = () => db.close();
      }),
  );
}

/**
 * Downscales and re-encodes before storing. A modern phone camera produces
 * 4–8 MB per shot; at 1280px and JPEG 82 these land around 150–250 KB, so a
 * year of weekly photos is still under 15 MB.
 */
export async function compressImage(file: File | Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    );
    // If the browser refuses to encode, storing the original beats losing it.
    return blob ?? file;
  } finally {
    bitmap.close();
  }
}

function uid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export async function savePhoto(
  file: File | Blob,
  meta: Omit<PhotoMeta, 'id' | 'createdAt'>,
): Promise<ProgressPhoto> {
  const blob = await compressImage(file);
  const photo: ProgressPhoto = {
    ...meta,
    id: uid(),
    createdAt: new Date().toISOString(),
    blob,
  };
  await tx('readwrite', (store) => store.put(photo) as unknown as IDBRequest<IDBValidKey>);
  return photo;
}

export async function listPhotos(): Promise<ProgressPhoto[]> {
  const all = await tx<ProgressPhoto[]>('readonly', (store) => store.getAll());
  return all.sort((a, b) => (a.date < b.date ? -1 : 1));
}

export async function deletePhoto(id: string): Promise<void> {
  await tx('readwrite', (store) => store.delete(id) as unknown as IDBRequest<undefined>);
}

export async function clearPhotos(): Promise<void> {
  await tx('readwrite', (store) => store.clear() as unknown as IDBRequest<undefined>);
}

/** Rough total size on disk, for the "you're using X MB" line. */
export function totalBytes(photos: ProgressPhoto[]): number {
  return photos.reduce((sum, p) => sum + (p.blob?.size ?? 0), 0);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
